/**
 * Style panel model (KHIYARSA-EDITOR-1 Phase 5, §B3).
 *
 * Iron principle: the panel writes KMD text and nothing else — a frontmatter
 * key, a `.tag`, or an inline key in the `{…}` block of one line. There is no
 * state behind the document, so anything the text cannot express simply has no
 * control (or a disabled one that says why).
 *
 * Presets are a shortcut layer on top, not the model: everything a preset does
 * is reachable one property at a time, and a preset is applied as ordinary
 * text the user can then edit.
 *
 * Pure and React-free so the read/write rules are unit-testable.
 */
import { COLOR_STYLE_KEYS, NODE_KINDS, STYLE_KEYS } from "../vocab";

import type { Family } from "../types";

// ---------------------------------------------------------------------------
// line targeting
// ---------------------------------------------------------------------------

export type LineKind =
  | "node"
  | "field"
  | "container"
  | "edge"
  | "note"
  | "directive"
  | "frontmatter"
  | "other";

export interface LineTarget {
  index: number;
  text: string;
  kind: LineKind;
  /** the `{…}` block contents, without braces */
  attrs: string;
  /** the label with attributes and any branch prefix removed */
  label: string;
}

const LIST_RE = /^(\s*)(?:[-*+]|\d+\.)\s+(.*)$/;
const HEADING_RE = /^(#{1,6})\s+(.*)$/;
const EDGE_RE = /\s(->|<->|--|\.\.>)\s/;
const ATTRS_RE = /\{([^}]*)\}\s*$/;

const linesOf = (text: string) => text.split("\n");

const frontmatterRange = (lines: string[]): [number, number] | null => {
  if (lines[0]?.trim() !== "---") {
    return null;
  }
  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trim() === "---") {
      return [0, i];
    }
  }
  return null;
};

const lineIndexAt = (text: string, caret: number): number => {
  const clamped = Math.max(0, Math.min(caret, text.length));
  return text.slice(0, clamped).split("\n").length - 1;
};

export const targetAt = (
  text: string,
  caret: number,
  family: Family,
): LineTarget => {
  const lines = linesOf(text);
  const index = Math.min(lineIndexAt(text, caret), lines.length - 1);
  const value = lines[index] ?? "";
  const fm = frontmatterRange(lines);
  const attrs = ATTRS_RE.exec(value)?.[1] ?? "";
  const withoutAttrs = value.replace(ATTRS_RE, "").trimEnd();

  let kind: LineKind = "other";
  let label = withoutAttrs.trim();

  if (fm && index >= fm[0] && index <= fm[1]) {
    kind = "frontmatter";
  } else if (HEADING_RE.test(value)) {
    kind = "container";
    label = HEADING_RE.exec(withoutAttrs)?.[2]?.trim() ?? label;
  } else if (/^\s*:{2,}/.test(value)) {
    kind = "directive";
  } else if (/^\s*>/.test(value)) {
    kind = "note";
  } else if (LIST_RE.test(value)) {
    const item = LIST_RE.exec(withoutAttrs)![2].trim();
    // in `er` a list item is a typed field row, not a node
    kind = family === "er" ? "field" : "node";
    label = item.replace(/^[^:]{1,30}:\s+/, "").trim();
  } else if (EDGE_RE.test(value)) {
    kind = "edge";
    label = withoutAttrs.trim();
  }

  return { index, text: value, kind, attrs, label };
};

// ---------------------------------------------------------------------------
// reading and writing the `{…}` block
// ---------------------------------------------------------------------------

/** split an attribute block into tokens, keeping quoted values intact */
const tokenize = (attrs: string): string[] => {
  const out: string[] = [];
  let current = "";
  let quoted = false;
  for (const char of attrs) {
    if (char === '"') {
      quoted = !quoted;
      current += char;
    } else if (/\s/.test(char) && !quoted) {
      if (current) {
        out.push(current);
      }
      current = "";
    } else {
      current += char;
    }
  }
  if (current) {
    out.push(current);
  }
  return out;
};

export interface ParsedStyle {
  id?: string;
  kind?: string;
  tags: string[];
  keys: Record<string, string>;
  /** barewords that are neither a kind nor a recognised flag */
  flags: string[];
}

export const parseStyle = (attrs: string): ParsedStyle => {
  const out: ParsedStyle = { tags: [], keys: {}, flags: [] };
  for (const token of tokenize(attrs)) {
    if (token.startsWith("#")) {
      out.id = token.slice(1);
    } else if (token.startsWith(".")) {
      out.tags.push(token.slice(1));
    } else if (token.includes("=")) {
      const at = token.indexOf("=");
      out.keys[token.slice(0, at)] = token.slice(at + 1).replace(/^"|"$/g, "");
    } else if ((NODE_KINDS as readonly string[]).includes(token)) {
      out.kind = token;
    } else {
      out.flags.push(token);
    }
  }
  return out;
};

const serializeStyle = (style: ParsedStyle): string => {
  const parts: string[] = [];
  if (style.id) {
    parts.push(`#${style.id}`);
  }
  if (style.kind) {
    parts.push(style.kind);
  }
  parts.push(...style.flags);
  parts.push(...style.tags.map((tag) => `.${tag}`));
  for (const [key, value] of Object.entries(style.keys)) {
    parts.push(/\s/.test(value) ? `${key}="${value}"` : `${key}=${value}`);
  }
  return parts.join(" ");
};

const rewriteLine = (line: string, style: ParsedStyle): string => {
  const body = line.replace(ATTRS_RE, "").trimEnd();
  const attrs = serializeStyle(style);
  return attrs ? `${body} {${attrs}}` : body;
};

/**
 * Set (or clear, with `null`) one inline key on the caret's line.
 * Returns the whole document.
 */
export const setLineKey = (
  text: string,
  caret: number,
  family: Family,
  key: string,
  value: string | null,
): string => {
  const target = targetAt(text, caret, family);
  if (target.kind === "frontmatter" || target.kind === "other") {
    return text;
  }
  const style = parseStyle(target.attrs);
  if (value === null) {
    delete style.keys[key];
  } else {
    style.keys[key] = value;
  }
  const lines = linesOf(text);
  lines[target.index] = rewriteLine(lines[target.index], style);
  return lines.join("\n");
};

/** add or remove a `.tag` on the caret's line */
export const toggleLineTag = (
  text: string,
  caret: number,
  family: Family,
  tag: string,
): string => {
  const target = targetAt(text, caret, family);
  if (target.kind === "frontmatter" || target.kind === "other") {
    return text;
  }
  const style = parseStyle(target.attrs);
  style.tags = style.tags.includes(tag)
    ? style.tags.filter((t) => t !== tag)
    : [...style.tags, tag];
  const lines = linesOf(text);
  lines[target.index] = rewriteLine(lines[target.index], style);
  return lines.join("\n");
};

/** set the node kind bareword on the caret's line */
export const setLineKind = (
  text: string,
  caret: number,
  family: Family,
  kind: string | null,
): string => {
  const target = targetAt(text, caret, family);
  if (target.kind !== "node") {
    return text;
  }
  const style = parseStyle(target.attrs);
  style.kind = kind ?? undefined;
  const lines = linesOf(text);
  lines[target.index] = rewriteLine(lines[target.index], style);
  return lines.join("\n");
};

// ---------------------------------------------------------------------------
// frontmatter
// ---------------------------------------------------------------------------

export const readFrontmatter = (text: string): Record<string, string> => {
  const lines = linesOf(text);
  const range = frontmatterRange(lines);
  const out: Record<string, string> = {};
  if (!range) {
    return out;
  }
  for (let i = range[0] + 1; i < range[1]; i++) {
    const match = /^([A-Za-z][\w-]*)\s*:\s*(.*)$/.exec(lines[i]);
    if (match && match[2] !== "") {
      out[match[1]] = match[2].trim();
    }
  }
  return out;
};

export const setFrontmatterKey = (
  text: string,
  key: string,
  value: string | null,
): string => {
  const lines = linesOf(text);
  const range = frontmatterRange(lines);
  if (!range) {
    return text;
  }
  const at = lines.findIndex(
    (line, i) =>
      i > range[0] && i < range[1] && new RegExp(`^${key}\\s*:`).test(line),
  );
  if (value === null) {
    return at === -1
      ? text
      : [...lines.slice(0, at), ...lines.slice(at + 1)].join("\n");
  }
  if (at === -1) {
    return [
      ...lines.slice(0, range[1]),
      `${key}: ${value}`,
      ...lines.slice(range[1]),
    ].join("\n");
  }
  lines[at] = `${key}: ${value}`;
  return lines.join("\n");
};

// ---------------------------------------------------------------------------
// what is actually enabled here
// ---------------------------------------------------------------------------

export interface Availability {
  enabled: boolean;
  /** why not, when disabled — the panel shows this rather than hiding silently */
  reason?: string;
}

/**
 * Whether a style key does anything on this target.
 *
 * Everything here is a fact about the emitter, recorded in §A5 of the contract:
 * `text-align` and `fill-pattern` reach node labels/bodies only, and `rounded`
 * is dropped on shapes that are not rectangles. A control the compiler would
 * accept but the emitter would ignore is worse than no control — it is hidden
 * state by another route.
 */
export const keyAvailability = (
  key: string,
  target: LineTarget,
  family: Family,
): Availability => {
  if (!STYLE_KEYS[key]) {
    return { enabled: false, reason: "notAKey" };
  }
  if (target.kind === "frontmatter" || target.kind === "other") {
    return { enabled: false, reason: "noTarget" };
  }

  const style = parseStyle(target.attrs);
  const shape = style.keys.shape ?? style.kind;

  switch (key) {
    case "text-align":
      return target.kind === "node"
        ? { enabled: true }
        : { enabled: false, reason: "nodesOnly" };
    case "fill-pattern":
      return target.kind === "node" || target.kind === "container"
        ? { enabled: true }
        : { enabled: false, reason: "nodesOnly" };
    case "rounded":
      return shape === "ellipse" ||
        shape === "diamond" ||
        shape === "decision" ||
        shape === "event"
        ? { enabled: false, reason: "rectOnly" }
        : { enabled: true };
    case "shape":
      return target.kind === "node"
        ? { enabled: true }
        : { enabled: false, reason: "nodesOnly" };
    default:
      return { enabled: true };
  }
};

/** frontmatter controls that are a no-op in this family (§A5) */
export const frontmatterAvailability = (
  key: string,
  family: Family,
): Availability => {
  if (key === "direction" && (family === "sequence" || family === "timeline")) {
    return { enabled: false, reason: "directionIgnored" };
  }
  if (key === "routing" && family !== "architecture") {
    return { enabled: false, reason: "routingArchOnly" };
  }
  return { enabled: true };
};

export const isColorKey = (key: string): boolean =>
  (COLOR_STYLE_KEYS as readonly string[]).includes(key);

// ---------------------------------------------------------------------------
// presets
// ---------------------------------------------------------------------------

export interface Preset {
  id: string;
  frontmatter: Record<string, string>;
  classes: Record<string, string>;
}

/**
 * Four shipped shortcuts. They carry no special status in the code — each is
 * frontmatter plus a `styles:` class map, written as ordinary text, so a user
 * can edit or delete any of it afterwards and build their own the same way.
 */
export const PRESETS: Preset[] = [
  {
    id: "blueprint",
    frontmatter: {
      theme: "mono",
      routing: "orthogonal",
      spacing: "comfortable",
    },
    classes: {
      accent: "{ stroke: blue.7, width: bold }",
      note: "{ fill: gray.1, line: dashed }",
    },
  },
  {
    id: "sketch",
    frontmatter: { theme: "default", routing: "curved" },
    classes: {
      hot: "{ stroke: red.7, width: bold }",
      faded: "{ opacity: 60 }",
    },
  },
  {
    id: "poster",
    frontmatter: { theme: "vibrant", spacing: "spacious" },
    classes: {
      hero: "{ font: display, font-size: l, width: bold }",
      sub: "{ font-size: s, fill: gray.1 }",
    },
  },
  {
    id: "docs",
    frontmatter: { theme: "default", spacing: "comfortable" },
    classes: {
      plain: "{ sketch: none, font: sans }",
      key: "{ fill: yellow.2, stroke: yellow.8 }",
    },
  },
];

/** apply a preset as text: frontmatter keys plus a `styles:` block */
export const applyPreset = (text: string, preset: Preset): string => {
  let out = text;
  for (const [key, value] of Object.entries(preset.frontmatter)) {
    out = setFrontmatterKey(out, key, value);
  }

  const lines = linesOf(out);
  const range = frontmatterRange(lines);
  if (!range) {
    return out;
  }
  // drop any existing styles: block, then write the preset's
  const existing = lines.findIndex(
    (line, i) => i > range[0] && i < range[1] && /^styles\s*:/.test(line),
  );
  if (existing !== -1) {
    let end = existing + 1;
    while (end < range[1] && /^\s+\S/.test(lines[end])) {
      end++;
    }
    lines.splice(existing, end - existing);
  }
  const at = frontmatterRange(lines)![1];
  const block = [
    "styles:",
    ...Object.entries(preset.classes).map(
      ([name, body]) => `  ${name}: ${body}`,
    ),
  ];
  return [...lines.slice(0, at), ...block, ...lines.slice(at)].join("\n");
};
